import axios from '@api';

import { transformChatSessionData, transformProject } from './utilities/helpers';
import chatApi from './api';

/**
 * Create a new chat session for the user on the chat service.
 * Chat session is authenticated and created for the user
 * based on the JWT header with the user ID in its claims.
 * Responds with the chat session UUID that was created,
 * which will be used for adding messages to and reading the chat history.
 * Create a new chat session for the user.
 * @return {Promise<string>} chatSessionId
 */
export const createChatSession = () => {
  return chatApi({
    data: {},
    method: 'post',
    url: '/api/v1/chat',
  }).then(({ data }) => {
    const sessionId = data?.chatSessionId;

    if (!sessionId) {
      throw new Error('Chat service did not include a session identifier in its response.');
    }
    return sessionId;
  });
};

/**
 * Sends a chat message to the chat service.
 * @param {Object} options
 * @param {string} options.chatSessionId
 * @param {string} options.userInput
 * @return {Promise<any>}
 */
export const createChatMessage = ({ chatSessionId, userInput }) => {
  return chatApi({
    data: userInput,
    method: 'post',
    url: `/api/v1/chat/${chatSessionId}`,
  }).then(({ data }) => data);
};

/**
 * Fetches a chat session from the chat service and transforms it for UI.
 * @param {Object} options
 * @param {string} options.chatSessionId
 * @param {(role: string) => string} [options.createFallbackId]
 * @return {Promise<{flowId: number|null, flowState: Object|null, messages: Array<{id: string, role: string, content: string, isStreaming?: boolean}>}>}
 */
export const readChatSession = ({ chatSessionId, createFallbackId }) => {
  return chatApi({
    method: 'get',
    url: `/api/v1/chat/${chatSessionId}`,
  }).then(({ data }) => transformChatSessionData(data, { createFallbackId }));
};

/**
 * Creates a project tied to a chat session.
 *
 * @param {Object} options
 * @param {string} options.chatSessionId chat session identifier
 * @param {Array<number>} [options.flowIds=[]] flows to associate on create
 * @param {string} [options.name] project name
 * @returns {Promise<ReturnType<typeof transformProject>>}
 */
export const createProject = ({ chatSessionId, flowIds = [], name }) => {
  return axios({
    data: {
      chat_session_id: chatSessionId,
      flow_ids: flowIds,
      name,
    },
    method: 'post',
    url: '/v2/api/projects',
    withCredentials: true,
  }).then(({ data }) => transformProject(data));
};

/**
 * Fetches a project by id.
 *
 * @param {Object} options
 * @param {number|string} options.projectId project identifier
 * @returns {Promise<ReturnType<typeof transformProject>>}
 */
export const readProject = ({ projectId }) => {
  return axios({
    method: 'get',
    url: `/v2/api/projects/${projectId}`,
    withCredentials: true,
  }).then(({ data }) => transformProject(data));
};

/**
 * Adds a flow to an existing project.
 *
 * @param {Object} options
 * @param {number|string} options.flowId flow identifier
 * @param {number|string} options.projectId project identifier
 * @returns {Promise<import('axios').AxiosResponse<any>>}
 */
export const addFlowToProject = ({ flowId, projectId }) => {
  return axios({
    method: 'put',
    url: `/v2/api/projects/${projectId}/add/${flowId}`,
    withCredentials: true,
  });
};

/**
 * Removes a flow from a project.
 *
 * @param {Object} options
 * @param {number|string} options.flowId flow identifier
 * @param {number|string} options.projectId project identifier
 * @returns {Promise<import('axios').AxiosResponse<any>>}
 */
export const removeFlowFromProject = ({ flowId, projectId }) => {
  return axios({
    method: 'put',
    url: `/v2/api/projects/${projectId}/remove/${flowId}`,
    withCredentials: true,
  });
};

/**
 * Updates the topic/component order stored in the chat flow state.
 *
 * @param {Object} options
 * @param {string} options.chatSessionId chat session identifier
 * @param {Array<string|number>} options.topicIds ordered list of topic/component identifiers
 * @returns {Promise<Object|null>} updated flow state, if provided by the API
 */
export const updateFlowStateTopicIds = ({ chatSessionId, topicIds = [] }) => {
  if (!chatSessionId) {
    return Promise.reject(new Error('chatSessionId is required to update the flow state.'));
  }

  return chatApi({
    data: { topic_ids: topicIds },
    method: 'put',
    url: `/api/v1/flow-state/${chatSessionId}`,
  }).then(({ data }) => {
    if (!data) return null;
    return data?.flow_state || data?.flowState || null;
  });
};

/**
 * Updates the display name of a project.
 *
 * @param {Object} options
 * @param {string} options.name new project name
 * @param {number|string} options.projectId project identifier
 * @returns {Promise<ReturnType<typeof transformProject>>}
 */
export const renameProject = ({ name, projectId }) => {
  return axios({
    data: { name },
    method: 'put',
    url: `/v2/api/projects/${projectId}/rename`,
    withCredentials: true,
  }).then(({ data }) => transformProject({ ...data, id: projectId, name }));
};

/**
 * Soft deletes a project.
 *
 * @param {Object} options
 * @param {number|string} options.projectId project identifier
 * @returns {Promise<import('axios').AxiosResponse<any>>}
 */
export const deleteProject = ({ projectId }) => {
  return axios({
    method: 'delete',
    url: `/v2/api/projects/${projectId}/delete`,
    withCredentials: true,
  });
};
